<template>
  <div class="rail-wrap">
    <div ref="sentinel" class="rail-sentinel" aria-hidden="true"></div>
    <section
      class="rail"
      :class="{ stuck: isStuck, compact: menusCompact }"
    >
      <p class="rail-label">{{ label }}</p>
      <p class="rail-hint">{{ t('missions.railHint') }}</p>
      <div class="track">
        <div
          class="line"
          :style="{ '--fill': `${fillPct}%` }"
        ></div>
        <ol class="nodes">
          <li
            v-for="step in steps"
            :key="step.index"
            class="node-wrap"
          >
            <button
              type="button"
              class="node"
              :class="{
                filled: step.filled,
                pending: step.pending,
                clickable: step.clickable,
                popping: popping.has(step.index),
              }"
              :disabled="!step.clickable"
              :aria-label="stepAria(step)"
              @click="onStepClick(step, $event)"
            >
              <span class="ring" aria-hidden="true"></span>
              <CoinToken
                :size="menusCompact ? 'sm' : 'md'"
                :shine="step.pending"
                :bob="step.pending"
              />
            </button>
          </li>
          <li class="node-wrap">
            <div
              ref="packNode"
              class="node pack"
              :class="{ filled: packOn, popping: popping.has(PACK_NODE) }"
              :aria-label="t('missions.packPrize')"
            >
              <img class="pack-art" :src="packCover" alt="">
            </div>
          </li>
        </ol>
      </div>
    </section>
    <MissionPackWin
      v-if="showWin"
      :cover="packCover"
      :dx="winOrigin.dx"
      :dy="winOrigin.dy"
      @close="onWinClose"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { MISSION_PACK_THRESHOLD } from '@shared/index'
import CoinToken from './CoinToken.vue'
import MissionPackWin from './MissionPackWin.vue'
import { flyCoinToWallet } from './coinFlight'
import { useMissionsStore } from '../stores/missions'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'
import { PACK_BY_ID, SHOP_PACK_ID } from '../game/packs'

const PACK_NODE = 99
const STEP_MS = 180
const packCover =
  PACK_BY_ID[SHOP_PACK_ID]?.cover ?? '/assets/packs/pack-master.webp'

type Step = {
  index: number
  filled: boolean
  pending: boolean
  claimed: boolean
  clickable: boolean
}

const { t } = useI18n()
const missions = useMissionsStore()
const { menusCompact } = useCompactLayout()
const WIN_KEY_PREFIX = 'yugioh.mission-pack-win:'

const sentinel = ref<HTMLElement | null>(null)
const packNode = ref<HTMLElement | null>(null)
const isStuck = ref(false)
const popping = ref(new Set<number>())
const showWin = ref(false)
const winOrigin = ref({ dx: 0, dy: 80 })
const visualFilled = ref(
  Math.min(MISSION_PACK_THRESHOLD, missions.railClaimed),
)
let observer: IntersectionObserver | null = null
let fillTimer: ReturnType<typeof setTimeout> | null = null
let celebrateTimer: ReturnType<typeof setTimeout> | null = null
let entered = false
let alive = true

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function clearFillTimer(): void {
  if (fillTimer) {
    clearTimeout(fillTimer)
    fillTimer = null
  }
}

function clearCelebrate(): void {
  if (celebrateTimer) {
    clearTimeout(celebrateTimer)
    celebrateTimer = null
  }
}

function winStorageKey(): string {
  return `${WIN_KEY_PREFIX}${missions.day}`
}

function hasSeenWin(): boolean {
  if (!missions.day) return true
  try {
    return localStorage.getItem(winStorageKey()) === '1'
  } catch {
    return false
  }
}

function markSeenWin(): void {
  if (!missions.day) return
  try {
    localStorage.setItem(winStorageKey(), '1')
  } catch {
    /* private mode */
  }
}

function captureOrigin(): void {
  const el = packNode.value
  if (!el) {
    winOrigin.value = { dx: 0, dy: 80 }
    return
  }
  const rect = el.getBoundingClientRect()
  winOrigin.value = {
    dx: rect.left + rect.width / 2 - window.innerWidth / 2,
    dy: rect.top + rect.height / 2 - window.innerHeight * 0.42,
  }
}

function scheduleWin(delay: number): void {
  if (showWin.value || hasSeenWin()) return
  clearCelebrate()
  celebrateTimer = window.setTimeout(() => {
    celebrateTimer = null
    if (!alive || showWin.value || hasSeenWin()) return
    if (visualFilled.value < MISSION_PACK_THRESHOLD) return
    if (!missions.packGranted) return
    if (missions.completed < MISSION_PACK_THRESHOLD) return
    captureOrigin()
    showWin.value = true
  }, delay)
}

function onWinClose(): void {
  markSeenWin()
  showWin.value = false
}

const steps = computed<Step[]>(() => {
  const unlocked = Math.min(missions.completed, MISSION_PACK_THRESHOLD)
  const claimed = missions.railClaimed
  const shown = visualFilled.value
  return Array.from({ length: MISSION_PACK_THRESHOLD }, (_, index) => {
    const filled = index < shown && index < unlocked
    return {
      index,
      filled,
      claimed: filled && index < claimed,
      pending: filled && index >= claimed,
      clickable: filled && index === claimed,
    }
  })
})

const packOn = computed(
  () => visualFilled.value >= MISSION_PACK_THRESHOLD,
)
const remaining = computed(() =>
  Math.max(0, MISSION_PACK_THRESHOLD - missions.completed),
)
const fillPct = computed(() => {
  const filled = Math.min(MISSION_PACK_THRESHOLD, visualFilled.value)
  return (filled / MISSION_PACK_THRESHOLD) * 100
})
const label = computed(() => {
  if (remaining.value <= 0) return t('missions.packUnlocked')
  if (remaining.value === 1) return t('missions.remainingOne')
  return t('missions.remaining', { n: remaining.value })
})

function stepAria(step: Step): string {
  if (step.pending) return t('missions.stepClaim', { n: step.index + 1 })
  if (step.claimed) return t('missions.stepClaimed', { n: step.index + 1 })
  if (step.filled) return t('missions.stepDone', { n: step.index + 1 })
  return t('missions.stepTodo', { n: step.index + 1 })
}

async function onStepClick(step: Step, event: Event): Promise<void> {
  if (!step.clickable || missions.busy) return
  const origin = event.currentTarget instanceof Element
    ? event.currentTarget
    : null
  await flyCoinToWallet(origin)
  await missions.claimStep()
}

function popRange(from: number, to: number): void {
  const next = new Set(popping.value)
  for (let i = from; i < to; i++) next.add(i)
  if (to >= MISSION_PACK_THRESHOLD) next.add(PACK_NODE)
  popping.value = next
  window.setTimeout(() => {
    const clear = new Set(popping.value)
    for (let i = from; i < to; i++) clear.delete(i)
    clear.delete(PACK_NODE)
    popping.value = clear
  }, 520)
}

function playFill(from: number, to: number): void {
  clearFillTimer()
  const target = Math.min(MISSION_PACK_THRESHOLD, Math.max(0, to))
  let current = Math.min(Math.max(0, from), target)
  visualFilled.value = current
  const crossed =
    from < MISSION_PACK_THRESHOLD && target >= MISSION_PACK_THRESHOLD
  if (prefersReducedMotion() || current >= target) {
    visualFilled.value = target
    if (crossed) scheduleWin(0)
    return
  }
  const step = () => {
    current += 1
    visualFilled.value = current
    popRange(current - 1, current)
    if (current >= target) {
      fillTimer = null
      if (crossed) scheduleWin(480)
      return
    }
    fillTimer = window.setTimeout(step, STEP_MS)
  }
  fillTimer = window.setTimeout(step, 80)
}

function syncFill(): void {
  const target = Math.min(MISSION_PACK_THRESHOLD, missions.completed)
  if (!missions.day) return
  if (target < visualFilled.value) {
    clearFillTimer()
    clearCelebrate()
    showWin.value = false
    visualFilled.value = Math.min(target, missions.railClaimed)
    return
  }
  if (target > visualFilled.value) {
    playFill(visualFilled.value, target)
    return
  }
  if (target >= MISSION_PACK_THRESHOLD && missions.packGranted) {
    scheduleWin(480)
  }
}

watch(
  () => `${missions.day}|${missions.completed}|${missions.packGranted}`,
  () => {
    if (!entered) return
    syncFill()
  },
)

onMounted(() => {
  const claimed = Math.min(MISSION_PACK_THRESHOLD, missions.railClaimed)
  visualFilled.value = claimed
  entered = true
  syncFill()
  observer = new IntersectionObserver(
    (entries) => {
      const hit = entries[0]
      isStuck.value = Boolean(hit && !hit.isIntersecting)
    },
    { threshold: 0, rootMargin: `-${getComputedStyle(document.documentElement)
      .getPropertyValue('--app-nav-offset') || '64px'} 0px 0px 0px` },
  )
  if (sentinel.value) observer.observe(sentinel.value)
})

onUnmounted(() => {
  alive = false
  clearFillTimer()
  clearCelebrate()
  observer?.disconnect()
})
</script>

<style scoped>
.rail-sentinel {
  height: 1px;
  pointer-events: none;
}

.rail {
  position: sticky;
  top: var(--app-nav-offset, 64px);
  z-index: 30;
  margin: 0 0 20px;
  padding: 12px 16px 14px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  transition:
    box-shadow var(--md-motion-short) var(--md-motion-standard);
}

.rail.stuck {
  box-shadow: var(--md-elevation-2);
  border-radius: 0;
}

.rail-label {
  margin: 0 0 4px;
  text-align: center;
  color: var(--md-sys-color-on-surface);
  font-size: 14px;
  font-weight: 500;
}

.rail-hint {
  margin: 0 0 10px;
  text-align: center;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 12px;
  font-weight: 400;
}

.track {
  position: relative;
}

.nodes {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.node-wrap {
  display: grid;
  place-items: center;
}

.line {
  position: absolute;
  left: 18px;
  right: 18px;
  top: 50%;
  height: 8px;
  border-radius: 999px;
  background: var(--md-sys-color-outline-variant);
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 0;
}

.line::after {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: var(--fill, 0%);
  border-radius: inherit;
  background: var(--mission-bar, var(--md-sys-color-primary));
  transition: width var(--md-motion-medium) var(--md-motion-emphasized);
}

.node {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--md-sys-color-surface-container);
  color: inherit;
  cursor: default;
  transition: transform var(--md-motion-medium) var(--md-motion-emphasized);
}

.node:not(.filled) :deep(.coin-token) {
  opacity: 0.38;
}

.node.filled :deep(.coin-token) {
  opacity: 1;
}

.node.pending .ring {
  opacity: 0.9;
  animation: ring-pulse 1.4s var(--md-motion-standard) infinite;
}

.node.clickable {
  cursor: pointer;
}

.node.clickable:hover,
.node.clickable:focus-visible {
  outline: none;
  transform: scale(1.08);
}

.node.popping {
  animation: node-pop var(--md-motion-medium) var(--md-motion-emphasized);
}

.node.pack {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
}

.node.pack:not(.filled) .pack-art {
  opacity: 0.38;
}

.pack-art {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ring {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid var(--mission-bar, var(--md-sys-color-primary));
  opacity: 0;
  pointer-events: none;
}

.node.popping .ring {
  animation: ring-out var(--md-motion-medium) var(--md-motion-emphasized);
}

.rail.compact .node {
  width: 28px;
  height: 28px;
}

.rail.compact .node.pack {
  width: 32px;
  height: 32px;
}

@keyframes node-pop {
  0% { transform: scale(1); }
  40% { transform: scale(1.18); }
  100% { transform: scale(1); }
}

@keyframes ring-out {
  0% { opacity: 0.9; transform: scale(0.85); }
  100% { opacity: 0; transform: scale(1.45); }
}

@keyframes ring-pulse {
  0%, 100% { opacity: 0.75; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}

@media (prefers-reduced-motion: reduce) {
  .node.popping,
  .node.popping .ring,
  .node.pending .ring,
  .line::after {
    animation: none;
    transition: none;
  }

  .node.pending .ring {
    opacity: 0.9;
    transform: none;
  }
}
</style>
